import { dayjs, toUtcAdjusted } from '@noor/lib';
import {
  adminCourierListSchema,
  adminCourierRowSchema,
  type AdminCourierList,
  type AdminCourierRow,
} from '../model/schema';
import { COURIER_REQUIREMENT_KEYS, ZERO_DATE } from './labels';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Tolerant list parse: rows that fail the schema are dropped instead of
 * failing the whole page; a bare array is accepted as an envelope-less page.
 */
export function toAdminCourierList(raw: unknown): AdminCourierList {
  const list: unknown[] =
    isRecord(raw) && Array.isArray(raw.data)
      ? (raw.data as unknown[])
      : Array.isArray(raw)
        ? (raw as unknown[])
        : [];
  const rows = list
    .map((item) => adminCourierRowSchema.safeParse(item).data)
    .filter((item): item is AdminCourierRow => item !== undefined);
  const envelope = adminCourierListSchema.safeParse({
    data: rows,
    last_page: isRecord(raw) ? raw.last_page : undefined,
    stats: isRecord(raw) ? raw.stats : undefined,
  });
  return envelope.success ? envelope.data : { data: rows };
}

export function getCourierRowId(row: {
  id?: string | number | null | undefined;
}): string | undefined {
  return row.id != null && row.id !== '' ? String(row.id) : undefined;
}

/** Name cell text (legacy `NameCell`): `<id> <first|Courier#> <last>`, else `Courier#<id>`. */
export function courierDisplayName(row: AdminCourierRow): string {
  const hasName = Boolean(row.first_name) || Boolean(row.last_name);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- legacy `||`: an empty first name reads as missing.
  const first = row.first_name || 'Courier#';
  const name = hasName
    ? `${first} ${row.last_name ?? ''}`.trim()
    : `Courier#${row.id ?? ''}`;
  return `${row.id ?? ''} ${name}`.trim();
}

/** `done / total` for the requirements cell (legacy counts truthy flags). */
export function requirementsProgress(row: AdminCourierRow): {
  done: number;
  total: number;
} {
  const done = COURIER_REQUIREMENT_KEYS.filter((key) => Boolean(row[key])).length;
  return { done, total: COURIER_REQUIREMENT_KEYS.length };
}

/** A usable timestamp: present, parseable and not the `0001-01-01` sentinel. */
export function isFormattableDate(date: string | null | undefined): date is string {
  return Boolean(date) && date !== ZERO_DATE;
}

/** Badge count for a status tab; `undefined` while stats are not loaded. */
export function statusCount(
  stats: Record<string, string | number> | null | undefined,
  countKey: string,
): number | undefined {
  const value = stats?.[countKey];
  return value === undefined ? undefined : Number(value);
}

/**
 * Digits-only amount input guard shared by the limit / bonus / foul dialogs
 * (legacy `/^\d+$/` + range check). Empty input is always allowed so the
 * field can be cleared; `minimum` is 0 for the limit, 1 for bonus and foul.
 */
export function acceptAmountInput(
  raw: string,
  { minimum, maximum }: { minimum: number; maximum: number },
): boolean {
  const value = raw.trim();
  if (value === '') return true;
  return /^\d+$/.test(value) && Number(value) >= minimum && Number(value) <= maximum;
}

/** DD/MM/YYYY typing mask for the contract date (legacy `DealAccepted`). */
export function maskDealDate(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length >= 5)
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

/** Contract date field seed: the stored deal date, else today, as DD/MM/YYYY. */
export function dealDateSeed(dealDate: string | null | undefined): string {
  return (dealDate ? dayjs(dealDate) : dayjs()).format('DD/MM/YYYY');
}

/**
 * Contract date → wire ISO. Legacy parsed the masked string with
 * `toUtcAdjusted` (dayjs falling back to `Date`), which is locale-fragile for
 * DD/MM/YYYY; the explicit format here is the deliberate hardening.
 */
export function dealDateToIso(masked: string): string | undefined {
  const parsed = dayjs(masked, 'DD/MM/YYYY', true);
  if (!parsed.isValid()) return undefined;
  return toUtcAdjusted(parsed.format('YYYY-MM-DD')).toISOString();
}
